import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, ShoppingBag, DollarSign, MessageCircle, ExternalLink, Loader2, Shield, RotateCcw } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPrice } from "@/utils/constants";
import api from "@/utils/api";
import { BuyerEscrowCard, SellerEscrowCard, EscrowStatusBadge } from "@/components/EscrowStatus";

const OrdersPage = ({ user, userCurrency }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('purchases');
  const [showShippingDialog, setShowShippingDialog] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [shippingRates, setShippingRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [selectedService, setSelectedService] = useState('');
  // Cancel order dialog states
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelSale, setCancelSale] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [packageWeight, setPackageWeight] = useState(16);
  // Return request states
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnOrder, setReturnOrder] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnDescription, setReturnDescription] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [myReturns, setMyReturns] = useState({});  // purchase_id -> return doc
  const [sellerReturns, setSellerReturns] = useState({}); // purchase_id -> return doc (seller side)

  useEffect(() => {
    if (!user) {
      toast.error("Please login to view your orders");
      navigate('/');
      return;
    }

    const fetchOrders = async () => {
      try {
        const [ordersRes, salesRes, carriersRes, returnsRes, sellerReturnsRes] = await Promise.all([
          api.get('/my-orders'),
          api.get('/my-sales'),
          api.get('/shipping/carriers'),
          api.get('/returns/my-returns').catch(() => ({ data: { returns: [] } })),
          api.get('/seller/return-requests').catch(() => ({ data: { returns: [] } }))
        ]);
        setOrders(ordersRes.data.orders || []);
        setSales(salesRes.data.sales || []);
        setCarriers(carriersRes.data.carriers || []);
        // Map returns by purchase_id for quick lookup
        const returnsMap = {};
        (returnsRes.data.returns || []).forEach(r => { returnsMap[r.purchase_id] = r; });
        setMyReturns(returnsMap);
        // Seller-side returns (so seller sees pending return requests to act on)
        const sellerMap = {};
        (sellerReturnsRes.data.returns || []).forEach(r => { sellerMap[r.purchase_id] = r; });
        setSellerReturns(sellerMap);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, navigate]);

  const getStatusBadge = (status) => {
    const styles = {
      'pending_payment': 'bg-amber-100 text-amber-800',
      'paid': 'bg-emerald-100 text-emerald-800',
      'shipped': 'bg-blue-100 text-blue-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'pending': 'bg-amber-100 text-amber-800',
      'completed': 'bg-emerald-100 text-emerald-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Pending';
  };

  // Submit a return request
  const submitReturnRequest = async () => {
    if (!returnOrder) return;
    if (!returnReason) {
      toast.error('Please choose a reason');
      return;
    }
    setSubmittingReturn(true);
    try {
      const res = await api.post('/returns/request', {
        purchase_id: returnOrder.purchase_id,
        reason: returnReason,
        description: returnDescription
      });
      toast.success('Return request submitted! The seller will review it.');
      // Refresh local state
      setMyReturns(prev => ({
        ...prev,
        [returnOrder.purchase_id]: {
          return_id: res.data.return_id,
          purchase_id: returnOrder.purchase_id,
          status: 'pending',
          reason: returnReason,
          description: returnDescription
        }
      }));
      setShowReturnDialog(false);
      setReturnOrder(null);
      setReturnReason('');
      setReturnDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit return request');
    } finally {
      setSubmittingReturn(false);
    }
  };

  // Determine if a buyer can request a return for an order
  const canRequestReturn = (order) => {
    // Already have a return for this purchase?
    if (myReturns[order.purchase_id]) return false;
    // Must be paid/shipped/delivered (not pending payment, not cancelled, not refunded)
    if (['pending_payment', 'cancelled', 'refunded'].includes(order.status)) return false;
    // Within 30 days of purchase
    if (order.purchased_at) {
      const days = (Date.now() - new Date(order.purchased_at).getTime()) / (1000 * 60 * 60 * 24);
      if (days > 30) return false;
    }
    return true;
  };

  // Seller responds to a return request
  const respondToReturn = async (returnId, action, purchaseId) => {
    if (action === 'reject' && !window.confirm('Reject this return request? The buyer will be notified.')) return;
    try {
      const res = await api.post(`/seller/returns/${returnId}/respond`, { action });
      toast.success(action === 'approve' ? 'Return approved. Buyer will be notified.' : 'Return rejected.');
      setSellerReturns(prev => ({
        ...prev,
        [purchaseId]: { ...(prev[purchaseId] || {}), status: res.data.status }
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  // Seller cancels an order with a reason + optional message
  const submitCancelOrder = async () => {
    if (!cancelSale || !cancelReason) return;
    setCancellingOrder(true);
    try {
      const res = await api.post(`/sales/${cancelSale.purchase_id}/cancel`, {
        reason: cancelReason,
        message: cancelMessage
      });
      const refundMsg = res.data.refund_status === 'completed' ? ' Buyer has been refunded.' :
                       res.data.refund_status === 'manual_required' ? ' Our team will handle the refund.' :
                       res.data.refund_status === 'failed' ? ' Refund failed — please contact support.' : '';
      toast.success(`Order cancelled.${refundMsg}`);
      setSales(prev => prev.map(s => s.purchase_id === cancelSale.purchase_id ? { ...s, status: 'cancelled' } : s));
      setShowCancelDialog(false);
      setCancelSale(null);
      setCancelReason('');
      setCancelMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancellingOrder(false);
    }
  };

  // Buyer creates a return shipping label after seller approves their return
  const buyerCreateReturnLabel = async (returnId, purchaseId) => {
    if (!window.confirm('Generate a return shipping label? The shipping cost is paid by you.')) return;
    try {
      const res = await api.post(`/returns/${returnId}/create-label`, { weight_oz: 16 });
      toast.success(`Return label created! Tracking: ${res.data.tracking_number}`);
      setMyReturns(prev => ({
        ...prev,
        [purchaseId]: {
          ...(prev[purchaseId] || {}),
          return_label_id: res.data.label_id,
          return_tracking_number: res.data.tracking_number,
          return_carrier: res.data.carrier,
          return_shipping_cost: res.data.shipping_cost,
          status: prev[purchaseId]?.status || 'approved'
        }
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create return label');
    }
  };

  const openShippingDialog = (sale) => {
    setSelectedSale(sale);
    setShowShippingDialog(true);
    setShippingRates([]);
    setSelectedCarrier('');
    setSelectedService('');
  };

  const fetchShippingRates = async () => {
    if (!selectedSale) return;
    setLoadingRates(true);
    try {
      const res = await api.post('/shipping/get-rates', {
        purchase_id: selectedSale.purchase_id,
        weight_oz: packageWeight
      });
      setShippingRates(res.data.rates || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch shipping rates");
    } finally {
      setLoadingRates(false);
    }
  };

  const createShippingLabel = async () => {
    if (!selectedSale || !selectedCarrier || !selectedService) {
      toast.error("Please select a shipping service");
      return;
    }
    setCreatingLabel(true);
    try {
      const res = await api.post('/shipping/create-label', {
        purchase_id: selectedSale.purchase_id,
        carrier_code: selectedCarrier,
        service_code: selectedService,
        weight_oz: packageWeight
      });
      toast.success(`Label created! Tracking: ${res.data.tracking_number}`);
      setShowShippingDialog(false);
      const salesRes = await api.get('/my-sales');
      setSales(salesRes.data.sales || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create shipping label");
    } finally {
      setCreatingLabel(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-52 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-52 pb-16 bg-slate-50" data-testid="orders-page">
      <div className="container mx-auto px-4 md:px-6">
        <h1 className="text-3xl font-bold mb-6">My Orders</h1>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <Button
            variant={activeTab === 'purchases' ? 'default' : 'outline'}
            onClick={() => setActiveTab('purchases')}
            className={activeTab === 'purchases' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            Purchases ({orders.length})
          </Button>
          <Button
            variant={activeTab === 'sales' ? 'default' : 'outline'}
            onClick={() => setActiveTab('sales')}
            className={activeTab === 'sales' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Sales ({sales.length})
          </Button>
        </div>

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <Card className="p-12 text-center">
                <ShoppingBag className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h2 className="text-xl font-semibold mb-2">No Orders Yet</h2>
                <p className="text-muted-foreground mb-4">Items you purchase will appear here</p>
                <Button onClick={() => navigate('/browse')} className="bg-emerald-500 hover:bg-emerald-600">
                  Start Shopping
                </Button>
              </Card>
            ) : (
              orders.map((order) => (
                <Card key={order.purchase_id} className="overflow-hidden" data-testid={`order-${order.purchase_id}`}>
                  <div className="flex flex-col md:flex-row">
                    <div className="w-full md:w-48 h-48 md:h-auto bg-slate-100">
                      {order.listing_image ? (
                        <img 
                          src={order.listing_image} 
                          alt={order.listing_title}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => navigate(`/item/${order.listing_id}`)}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-12 w-12 text-slate-300" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 
                            className="font-semibold text-lg cursor-pointer hover:text-emerald-600"
                            onClick={() => navigate(`/item/${order.listing_id}`)}
                          >
                            {order.listing_title}
                          </h3>
                          <p className="text-sm text-muted-foreground">Seller: {order.seller_name}</p>
                        </div>
                        <Badge className={getStatusBadge(order.status)}>{formatStatus(order.status)}</Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Item Price</p>
                          <p className="font-semibold">{formatPrice(order.item_price, 'USD', userCurrency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Shipping</p>
                          <p className="font-semibold">
                            {order.shipping_cost > 0 ? formatPrice(order.shipping_cost, 'USD', userCurrency) : 'Free'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Total Paid</p>
                          <p className="font-bold text-emerald-600">{formatPrice(order.total_paid, 'USD', userCurrency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Date</p>
                          <p className="font-semibold">
                            {order.purchased_at ? new Date(order.purchased_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Tracking Info (Buyer side) */}
                      {order.tracking_number && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`buyer-tracking-${order.purchase_id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-blue-900 font-medium uppercase tracking-wide">Tracking</p>
                              <p className="font-mono font-bold text-blue-800 mt-1 truncate">{order.tracking_number}</p>
                              <p className="text-xs text-blue-700 mt-1">
                                Carrier: {order.carrier?.toUpperCase() || order.shipping_carrier?.toUpperCase() || 'N/A'}
                                {order.delivery_status ? ` · ${order.delivery_status}` : ''}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-300 text-blue-700 hover:bg-blue-100"
                              onClick={() => {
                                const carrier = (order.carrier || order.shipping_carrier || '').toLowerCase();
                                let trackingUrl = '#';
                                if (carrier.includes('usps')) trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.tracking_number}`;
                                else if (carrier.includes('ups')) trackingUrl = `https://www.ups.com/track?tracknum=${order.tracking_number}`;
                                else if (carrier.includes('fedex')) trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${order.tracking_number}`;
                                else if (carrier.includes('dhl')) trackingUrl = `https://www.dhl.com/en/express/tracking.html?AWB=${order.tracking_number}`;
                                else trackingUrl = `https://www.google.com/search?q=${encodeURIComponent(order.tracking_number + ' tracking')}`;
                                window.open(trackingUrl, '_blank', 'noopener,noreferrer');
                              }}
                              data-testid={`buyer-track-btn-${order.purchase_id}`}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" /> Track Package
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Buyer Escrow Protection Card */}
                      {order.escrow_status && (
                        <div className="mt-4">
                          <BuyerEscrowCard 
                            purchase={{
                              purchase_id: order.purchase_id,
                              escrow_status: order.escrow_status,
                              shipping_tracking: order.tracking_number,
                              buyer_confirmed_delivery: order.buyer_confirmed_delivery
                            }} 
                            onUpdate={() => window.location.reload()}
                          />
                        </div>
                      )}
                      
                      <div className="flex gap-2 mt-4 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/item/${order.listing_id}`)}>
                          View Item
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => navigate('/messages')}>
                          <MessageCircle className="h-4 w-4 mr-1" /> Contact Seller
                        </Button>
                        {myReturns[order.purchase_id] ? (
                          <Badge className={
                            myReturns[order.purchase_id].status === 'pending' ? 'bg-amber-100 text-amber-800' :
                            myReturns[order.purchase_id].status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                            myReturns[order.purchase_id].status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          } data-testid={`return-status-${order.purchase_id}`}>
                            <RotateCcw className="h-3 w-3 mr-1" /> Return {formatStatus(myReturns[order.purchase_id].status)}
                          </Badge>
                        ) : canRequestReturn(order) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-300 text-orange-700 hover:bg-orange-50"
                            onClick={() => {
                              setReturnOrder(order);
                              setReturnReason('');
                              setReturnDescription('');
                              setShowReturnDialog(true);
                            }}
                            data-testid={`request-return-btn-${order.purchase_id}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" /> Request Return
                          </Button>
                        ) : null}
                      </div>

                      {/* Return label actions (buyer side) — show when return approved */}
                      {myReturns[order.purchase_id]?.status === 'approved' && (
                        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <p className="text-sm font-medium text-emerald-900 mb-2">
                            ✅ Return approved! Ship the item back to the seller.
                          </p>
                          {myReturns[order.purchase_id].return_label_id ? (
                            <>
                              <div className="text-xs text-emerald-800 mb-2">
                                Tracking: <span className="font-mono font-semibold">{myReturns[order.purchase_id].return_tracking_number}</span>
                                {myReturns[order.purchase_id].return_shipping_cost ? ` · $${myReturns[order.purchase_id].return_shipping_cost.toFixed(2)}` : ''}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                  onClick={() => {
                                    const token = localStorage.getItem('token');
                                    const apiBase = process.env.REACT_APP_BACKEND_URL;
                                    window.open(`${apiBase}/api/shipping/labels/${order.purchase_id}/print?token=${encodeURIComponent(token)}&size=letter`, '_blank');
                                  }}
                                  data-testid={`buyer-print-return-letter-${order.purchase_id}`}
                                >
                                  <Package className="h-4 w-4 mr-1" /> Print 8.5×11
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                  onClick={() => {
                                    const token = localStorage.getItem('token');
                                    const apiBase = process.env.REACT_APP_BACKEND_URL;
                                    window.open(`${apiBase}/api/shipping/labels/${order.purchase_id}/print?token=${encodeURIComponent(token)}&size=4x6`, '_blank');
                                  }}
                                  data-testid={`buyer-print-return-4x6-${order.purchase_id}`}
                                >
                                  <Package className="h-4 w-4 mr-1" /> Print 4×6
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const token = localStorage.getItem('token');
                                    const apiBase = process.env.REACT_APP_BACKEND_URL;
                                    window.open(`${apiBase}/api/shipping/labels/${order.purchase_id}/download?token=${encodeURIComponent(token)}`, '_blank');
                                  }}
                                  data-testid={`buyer-download-return-${order.purchase_id}`}
                                >
                                  <ExternalLink className="h-4 w-4 mr-1" /> Download PDF
                                </Button>
                              </div>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600 text-white"
                              onClick={() => buyerCreateReturnLabel(myReturns[order.purchase_id].return_id, order.purchase_id)}
                              data-testid={`buyer-create-return-label-${order.purchase_id}`}
                            >
                              <Package className="h-4 w-4 mr-1" /> Get Return Label (paid by you)
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <div className="space-y-4">
            {sales.length === 0 ? (
              <Card className="p-12 text-center">
                <DollarSign className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h2 className="text-xl font-semibold mb-2">No Sales Yet</h2>
                <p className="text-muted-foreground mb-4">Items you sell will appear here</p>
                <Button onClick={() => navigate('/sell')} className="bg-emerald-500 hover:bg-emerald-600">
                  Start Selling
                </Button>
              </Card>
            ) : (
              sales.map((sale) => (
                <Card key={sale.purchase_id} className="overflow-hidden" data-testid={`sale-${sale.purchase_id}`}>
                  <div className="flex flex-col md:flex-row">
                    <div className="w-full md:w-48 h-48 md:h-auto bg-slate-100">
                      {sale.listing_image ? (
                        <img src={sale.listing_image} alt={sale.listing_title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-12 w-12 text-slate-300" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-lg">{sale.listing_title}</h3>
                          <p className="text-sm text-muted-foreground">Buyer: {sale.buyer_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {sale.tracking_number ? (
                            <Badge className="bg-blue-100 text-blue-800">Shipped</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800">Needs Shipping</Badge>
                          )}
                          <Badge className={getStatusBadge(sale.payout_status)}>
                            Payout: {formatStatus(sale.payout_status)}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Sale Price</p>
                          <p className="font-semibold">{formatPrice(sale.item_price, 'USD', userCurrency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">BidZuu Fee (10%)</p>
                          <p className="font-semibold text-red-500">-{formatPrice(sale.bidzuu_fee, 'USD', userCurrency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Tax on Fee</p>
                          <p className="font-semibold text-red-500">-{formatPrice(sale.tax_on_fee, 'USD', userCurrency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Your Payout</p>
                          <p className="font-bold text-emerald-600">{formatPrice(sale.seller_payout, 'USD', userCurrency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Sold Date</p>
                          <p className="font-semibold">
                            {sale.sold_at ? new Date(sale.sold_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Ship-To block — shown so seller can print a shipping label */}
                      {sale.shipping_address ? (
                        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid={`ship-to-${sale.purchase_id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm">
                              <p className="font-semibold text-emerald-900 mb-1">Ship To</p>
                              <p className="font-medium text-slate-900">{sale.shipping_address.name || sale.buyer_name}</p>
                              <p className="text-slate-700">
                                {sale.shipping_address.street}
                                {sale.shipping_address.street2 ? `, ${sale.shipping_address.street2}` : ''}
                              </p>
                              <p className="text-slate-700">
                                {sale.shipping_address.city}{sale.shipping_address.city ? ', ' : ''}
                                {sale.shipping_address.state} {sale.shipping_address.zip_code}
                              </p>
                              <p className="text-slate-700">{sale.shipping_address.country || 'US'}</p>
                              {(sale.buyer_phone || sale.shipping_address.phone) && (
                                <p className="text-slate-700 mt-1">📞 {sale.buyer_phone || sale.shipping_address.phone}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const a = sale.shipping_address;
                                const text = `${a.name || sale.buyer_name}\n${a.street || ''}${a.street2 ? ', ' + a.street2 : ''}\n${a.city || ''}, ${a.state || ''} ${a.zip_code || ''}\n${a.country || 'US'}${(sale.buyer_phone || a.phone) ? '\n' + (sale.buyer_phone || a.phone) : ''}`;
                                navigator.clipboard?.writeText(text);
                                toast.success('Address copied');
                              }}
                              className="text-xs px-2 py-1 rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100"
                              data-testid={`copy-ship-${sale.purchase_id}`}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          Buyer shipping address not on file. (Older order — ask buyer in chat.)
                        </div>
                      )}

                      {/* Seller Escrow Status Card */}
                      <div className="mt-4">
                        <SellerEscrowCard 
                          purchase={{
                            purchase_id: sale.purchase_id,
                            escrow_status: sale.escrow_status || 'holding',
                            seller_shipped: sale.seller_shipped || !!sale.tracking_number,
                            shipping_tracking: sale.tracking_number,
                            escrow_release_date: sale.escrow_release_date,
                            seller_payout: sale.seller_payout,
                            dispute_status: sale.dispute_status
                          }} 
                          onUpdate={() => window.location.reload()}
                        />
                      </div>

                      {/* Tracking Info */}
                      {sale.tracking_number && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-blue-600 font-medium">Tracking Number</p>
                              <p className="font-mono font-bold text-blue-800">{sale.tracking_number}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Carrier: {sale.carrier?.toUpperCase() || 'N/A'}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-300 text-blue-700 hover:bg-blue-100"
                              onClick={() => {
                                const carrier = sale.carrier?.toLowerCase();
                                let trackingUrl = '#';
                                if (carrier === 'usps') trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${sale.tracking_number}`;
                                else if (carrier === 'ups') trackingUrl = `https://www.ups.com/track?tracknum=${sale.tracking_number}`;
                                else if (carrier === 'fedex') trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${sale.tracking_number}`;
                                window.open(trackingUrl, '_blank');
                              }}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" /> Track Package
                            </Button>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex-1 min-w-[140px]"
                              onClick={() => {
                                const token = localStorage.getItem('token');
                                const apiBase = process.env.REACT_APP_BACKEND_URL;
                                const url = `${apiBase}/api/shipping/labels/${sale.purchase_id}/print?token=${encodeURIComponent(token)}&size=letter`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              data-testid={`print-letter-${sale.purchase_id}`}
                            >
                              <Package className="h-4 w-4 mr-1" /> Print 8.5×11
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex-1 min-w-[120px]"
                              onClick={() => {
                                const token = localStorage.getItem('token');
                                const apiBase = process.env.REACT_APP_BACKEND_URL;
                                const url = `${apiBase}/api/shipping/labels/${sale.purchase_id}/print?token=${encodeURIComponent(token)}&size=4x6`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              data-testid={`print-4x6-${sale.purchase_id}`}
                            >
                              <Package className="h-4 w-4 mr-1" /> Print 4×6
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-300 text-slate-700 hover:bg-slate-50 flex-1 min-w-[140px]"
                              onClick={() => {
                                const token = localStorage.getItem('token');
                                const apiBase = process.env.REACT_APP_BACKEND_URL;
                                const url = `${apiBase}/api/shipping/labels/${sale.purchase_id}/download?token=${encodeURIComponent(token)}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              data-testid={`download-label-${sale.purchase_id}`}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" /> Download PDF
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2 mt-4 flex-wrap">
                        {!sale.tracking_number && (
                          <Button 
                            size="sm" 
                            className="bg-emerald-500 hover:bg-emerald-600"
                            onClick={() => openShippingDialog(sale)}
                            data-testid={`create-label-btn-${sale.purchase_id}`}
                          >
                            <Package className="h-4 w-4 mr-1" /> Create Shipping Label
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => navigate('/messages')}>
                          <MessageCircle className="h-4 w-4 mr-1" /> Contact Buyer
                        </Button>
                        {!['cancelled', 'refunded', 'delivered'].includes(sale.status) && !sale.tracking_number && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setCancelSale(sale);
                              setCancelReason('');
                              setCancelMessage('');
                              setShowCancelDialog(true);
                            }}
                            data-testid={`cancel-order-btn-${sale.purchase_id}`}
                          >
                            Cancel Order
                          </Button>
                        )}
                        {sale.status === 'cancelled' && (
                          <Badge className="bg-red-100 text-red-800">Cancelled</Badge>
                        )}
                      </div>

                      {/* Return request banner (visible to seller) */}
                      {sellerReturns[sale.purchase_id] && (
                        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid={`seller-return-${sale.purchase_id}`}>
                          <div className="flex items-start gap-3">
                            <RotateCcw className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-orange-900">Return Request</p>
                                <Badge className={
                                  sellerReturns[sale.purchase_id].status === 'pending' ? 'bg-amber-100 text-amber-800' :
                                  sellerReturns[sale.purchase_id].status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                                  sellerReturns[sale.purchase_id].status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-blue-100 text-blue-800'
                                }>
                                  {formatStatus(sellerReturns[sale.purchase_id].status)}
                                </Badge>
                              </div>
                              {sellerReturns[sale.purchase_id].reason && (
                                <p className="text-sm text-orange-900 mt-1">
                                  <span className="font-medium">Reason:</span> {formatStatus(sellerReturns[sale.purchase_id].reason)}
                                </p>
                              )}
                              {sellerReturns[sale.purchase_id].description && (
                                <p className="text-sm text-orange-800 mt-1 italic">"{sellerReturns[sale.purchase_id].description}"</p>
                              )}
                              {sellerReturns[sale.purchase_id].status === 'pending' && (
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    className="bg-emerald-500 hover:bg-emerald-600"
                                    onClick={() => respondToReturn(sellerReturns[sale.purchase_id].return_id, 'approve', sale.purchase_id)}
                                    data-testid={`approve-return-${sale.purchase_id}`}
                                  >
                                    Approve Return
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50"
                                    onClick={() => respondToReturn(sellerReturns[sale.purchase_id].return_id, 'reject', sale.purchase_id)}
                                    data-testid={`reject-return-${sale.purchase_id}`}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Shipping Label Dialog */}
        <Dialog open={showShippingDialog} onOpenChange={setShowShippingDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-600" />
                Create Shipping Label
              </DialogTitle>
            </DialogHeader>
            
            {selectedSale && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedSale.listing_title}</p>
                  <p className="text-sm text-muted-foreground">Buyer: {selectedSale.buyer_name}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium">Package Weight (oz)</Label>
                  <Input
                    type="number"
                    value={packageWeight}
                    onChange={(e) => setPackageWeight(Number(e.target.value))}
                    min={1}
                    max={1120}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {(packageWeight / 16).toFixed(2)} lbs
                  </p>
                </div>

                {shippingRates.length === 0 && (
                  <Button
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    onClick={fetchShippingRates}
                    disabled={loadingRates}
                  >
                    {loadingRates ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading Rates...</>
                    ) : (
                      <>Get Shipping Rates</>
                    )}
                  </Button>
                )}

                {shippingRates.length > 0 && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    <Label className="text-sm font-medium">Select Shipping Service</Label>
                    {shippingRates.map((rate) => (
                      <div
                        key={rate.service_code}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedService === rate.service_code
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => {
                          setSelectedCarrier(rate.carrier_code);
                          setSelectedService(rate.service_code);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{rate.carrier_code.toUpperCase()}</p>
                            <p className="text-sm text-muted-foreground">{rate.service_name}</p>
                            <p className="text-xs text-muted-foreground">{rate.estimated_days}</p>
                          </div>
                          <p className="text-lg font-bold text-emerald-600">${rate.rate.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {shippingRates.length > 0 && (
                  <Button
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    onClick={createShippingLabel}
                    disabled={creatingLabel || !selectedService}
                  >
                    {creatingLabel ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating Label...</>
                    ) : (
                      <>
                        <Package className="h-4 w-4 mr-2" />
                        Create Label {selectedService && `($${shippingRates.find(r => r.service_code === selectedService)?.rate.toFixed(2) || '0.00'})`}
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Return Request Dialog */}
        <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-orange-600" />
                Request Return
              </DialogTitle>
            </DialogHeader>
            {returnOrder && (
              <div className="space-y-4">
                <div className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                  {returnOrder.listing_image && (
                    <img src={returnOrder.listing_image} alt="" className="w-16 h-16 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-2">{returnOrder.listing_title}</p>
                    <p className="text-xs text-muted-foreground">Order #{returnOrder.purchase_id}</p>
                    <p className="text-sm font-semibold text-emerald-600 mt-1">{formatPrice(returnOrder.item_price || returnOrder.amount, userCurrency)}</p>
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Reason for return *</Label>
                  <Select value={returnReason} onValueChange={setReturnReason}>
                    <SelectTrigger data-testid="return-reason-select">
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_as_described">Item not as described</SelectItem>
                      <SelectItem value="damaged">Item arrived damaged</SelectItem>
                      <SelectItem value="wrong_item">Received wrong item</SelectItem>
                      <SelectItem value="defective">Item is defective / doesn't work</SelectItem>
                      <SelectItem value="missing_parts">Missing parts or accessories</SelectItem>
                      <SelectItem value="counterfeit">Counterfeit / authenticity concern</SelectItem>
                      <SelectItem value="changed_mind">Changed my mind</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="mb-2 block">Details (optional)</Label>
                  <Textarea
                    placeholder="Describe what's wrong with the item so the seller can review your request..."
                    value={returnDescription}
                    onChange={(e) => setReturnDescription(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    data-testid="return-description-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{returnDescription.length}/1000</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  <p className="font-semibold mb-1">How returns work on BidZuu</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>The seller has 3 days to approve or reject your request.</li>
                    <li>If approved, ship the item back. Once received, your refund is released.</li>
                    <li>If rejected and you disagree, contact BidZuu support.</li>
                    <li>Returns must be requested within 30 days of purchase.</li>
                  </ul>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowReturnDialog(false)} disabled={submittingReturn}>
                    Cancel
                  </Button>
                  <Button
                    onClick={submitReturnRequest}
                    disabled={submittingReturn || !returnReason}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="submit-return-btn"
                  >
                    {submittingReturn ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      'Submit Return Request'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Cancel Order Dialog (seller) */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-red-600">Cancel Order</DialogTitle>
            </DialogHeader>
            {cancelSale && (
              <div className="space-y-4">
                <div className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                  {cancelSale.listing_image && (
                    <img src={cancelSale.listing_image} alt="" className="w-16 h-16 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-2">{cancelSale.listing_title}</p>
                    <p className="text-xs text-muted-foreground">Order #{cancelSale.purchase_id}</p>
                    <p className="text-sm font-semibold text-emerald-600 mt-1">{formatPrice(cancelSale.item_price, userCurrency)}</p>
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Reason for cancellation *</Label>
                  <Select value={cancelReason} onValueChange={setCancelReason}>
                    <SelectTrigger data-testid="cancel-reason-select">
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="out_of_stock">Item is out of stock / sold elsewhere</SelectItem>
                      <SelectItem value="damaged">Item is damaged</SelectItem>
                      <SelectItem value="cannot_ship">Cannot ship to buyer's location</SelectItem>
                      <SelectItem value="pricing_error">Pricing or listing error</SelectItem>
                      <SelectItem value="buyer_request">Buyer requested cancellation</SelectItem>
                      <SelectItem value="payment_issue">Payment issue</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="mb-2 block">Message to buyer (optional)</Label>
                  <Textarea
                    placeholder="Explain to the buyer why you're cancelling. They'll receive this in their inbox + email."
                    value={cancelMessage}
                    onChange={(e) => setCancelMessage(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    data-testid="cancel-message-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{cancelMessage.length}/1000</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  <p className="font-semibold mb-1">What happens when you cancel:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>The buyer will be refunded automatically (Stripe / PayPal).</li>
                    <li>The buyer is notified via email + chat with your reason.</li>
                    <li>Your listing is restocked and put back on the market.</li>
                    <li>Cancellation cannot be undone. You can only cancel before shipping.</li>
                  </ul>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowCancelDialog(false)} disabled={cancellingOrder}>
                    Keep Order
                  </Button>
                  <Button
                    onClick={submitCancelOrder}
                    disabled={cancellingOrder || !cancelReason}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    data-testid="confirm-cancel-btn"
                  >
                    {cancellingOrder ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cancelling...</>
                    ) : (
                      'Cancel Order & Refund Buyer'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default OrdersPage;
